import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { verifyCode } from '../../services/auth/emailVerification';
import { createAccount } from '../../services/auth/authService';
import { doc, setDoc } from 'firebase/firestore';
import { db, auth } from '../../config/firebase';
import { queueEmail } from '../../services/emailService';
import { Button } from '../common/Button';
import { Input } from '../common/FormFields';
import { Mail, AlertTriangle } from 'lucide-react';
import { logError, logInfo } from '../../utils/logger';
import { sendVerificationEmail } from '../../services/auth/emailVerification';

export const VerificationPage = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const [resending, setResending] = useState(false);

  useEffect(() => {
    console.log('[VerificationPage] Component mounted');
    const pendingEmail = localStorage.getItem('pendingEmail');
    const formData = localStorage.getItem('signupFormData');
    const signupType = localStorage.getItem('signupType');
    
    if (!pendingEmail || !formData || !signupType) {
      console.log('[VerificationPage] Missing required data, redirecting to signup');
      navigate('/signup/free');
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const handleResendCode = async () => {
    setResending(true);
    setError('');
    
    try {
      const pendingEmail = localStorage.getItem('pendingEmail');
      if (!pendingEmail) {
        throw new Error('No email found for verification');
      }

      await sendVerificationEmail(pendingEmail);
      setTimeLeft(300); // Reset timer to 5 minutes
      logInfo('Verification code resent successfully');
    } catch (err) {
      logError('Failed to resend verification code:', err);
      setError('Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async () => {
    if (!code.trim()) return;
    
    setLoading(true);
    try {
      console.log('[VerificationPage] Verifying code');
      await verifyCode(code);

      // Get stored form data
      const formDataStr = localStorage.getItem('signupFormData');
      const signupType = localStorage.getItem('signupType');
      
      if (!formDataStr || !signupType) {
        throw new Error('Missing signup data');
      }

      const formData = JSON.parse(formDataStr);
      if (!formData.email || !formData.password) {
        throw new Error('Missing required account fields');
      }

      console.log('[VerificationPage] Creating account');
      await createAccount(formData.email, formData.password);

      // Handle different signup types
      switch (signupType) {
        case 'free':
          navigate('/shared');
          break;

        case 'beta':
          const now = new Date();
          
          // Create subscription with pending approval status
          await setDoc(doc(db, 'subscriptions', auth.currentUser.uid), {
            status: 'pending_approval',
            plan: 'beta',
            needsApproval: true,
            isValid: false,
            createdAt: now.toISOString(),
            updatedAt: now.toISOString()
          });

          // Send admin notification
          await queueEmail('admin@oversizehub.net', 'New Beta User Approval Required', {
            template: 'BETA_APPROVAL_REQUEST',
            variables: {
              userName: formData.name,
              userEmail: formData.email,
              company: formData.company,
              role: formData.role,
              approvalUrl: `${window.location.origin}/admin/beta-approvals/${formData.email}`
            }
          });

          navigate('/beta-request-submitted');
          break;

        case 'paid':
          if (formData.selectedPlan === 'enterprise') {
            navigate('/enterprise-inquiry');
          } else {
            navigate('/professional/options');
          }
          break;

        default:
          navigate('/');
      }

      // Clear signup data
      localStorage.removeItem('pendingEmail');
      localStorage.removeItem('signupType');
      localStorage.removeItem('signupFormData');
      localStorage.removeItem('verificationCode');
      localStorage.removeItem('verificationExpiry');
      localStorage.removeItem('emailVerified');

    } catch (error) {
      console.error('[VerificationPage] Error:', error);
      logError('Verification failed:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#3D3F42] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        {/* Logo */}
        <div className="flex justify-center mb-12">
          <img src="/logo.png" alt="OverSizeHub" className="h-16" />
        </div>

        {/* Main Card */}
        {/* Main Card */}
        <div className="bg-[#2A2B2E] rounded-lg">
          <div className="text-center mb-8">
            <div className="bg-red-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-white">Verify Your Email</h2>
            <p className="text-gray-400 mt-2">
              Enter the 6-digit code sent to {localStorage.getItem('pendingEmail')}
            </p>
          </div>

          <div className="p-6 space-y-6">
            {error && (
              <div className="bg-red-500/10 text-red-400 p-4 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-6">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                maxLength={6}
                className="text-center text-2xl tracking-widest bg-gray-700/30 text-white"
              />

              <Button
                onClick={handleSubmit}
                disabled={code.length !== 6 || loading}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
              >
                {loading ? 'Verifying...' : 'Verify Email'}
              </Button>

              {timeLeft > 0 ? (
                <p className="text-sm text-gray-400 text-center">
                  Resend code in {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                </p>
              ) : (
                <Button
                  variant="default"
                  onClick={handleResendCode}
                  loading={resending}
                  disabled={resending}
                  className="w-full bg-gray-700 hover:bg-gray-600 text-white"
                >
                  {resending ? 'Sending...' : 'Resend Code'}
                </Button>
              )}

              <div className="border-t border-gray-700 pt-6 mt-6 text-center">
                <p className="text-gray-400 text-sm">
                  Already a member?{' '}
                  <button
                    onClick={() => navigate('/login')}
                    className="text-red-400 hover:text-red-300"
                  >
                    Sign in
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};